/**
 * Manage App Organizations Modal
 * Modal pour gérer les organizations ayant accès à une app (super admin only)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, Check, Building2 } from 'lucide-react';
import { useUpdateAppOrganizations, useSuperAdminOrganizations } from '@/hooks/use-super-admin';

interface App {
  id: string;
  name: string;
  slug: string;
  organizationIds: string[];
  color: string | null;
}

interface ManageAppOrganizationsModalProps {
  app: App;
  onClose: () => void;
}

export const ManageAppOrganizationsModal: React.FC<ManageAppOrganizationsModalProps> = ({
  app,
  onClose,
}) => {
  const { t } = useTranslation();
  const [editedOrgIds, setEditedOrgIds] = useState<string[]>(app.organizationIds || []);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const updateMutation = useUpdateAppOrganizations();

  // Récupérer les organizations depuis Clerk
  const { data: orgsData, isLoading: orgsLoading } = useSuperAdminOrganizations();
  const organizations = orgsData?.organizations || [];

  // Filtrer les organisations non encore ajoutées
  const availableOrgs = organizations.filter((org) => !editedOrgIds.includes(org.id));

  // Helper pour trouver une organization par ID
  const getOrgById = (orgId: string) => {
    return organizations.find((org) => org.id === orgId);
  };

  // Fermer le select quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    if (isSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelectOpen]);

  const handleAddOrganization = (orgId: string) => {
    if (!orgId.trim()) return;

    if (editedOrgIds.includes(orgId.trim())) {
      alert('Cette organisation est déjà dans la liste');
      return;
    }

    setEditedOrgIds([...editedOrgIds, orgId.trim()]);
    setIsSelectOpen(false);
  };

  const handleRemoveOrganization = (orgId: string) => {
    setEditedOrgIds(editedOrgIds.filter((id) => id !== orgId));
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        appId: app.id,
        organizationIds: editedOrgIds,
      });
      onClose();
    } catch (error) {
      console.error('Error updating app organizations:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const hasChanges =
    JSON.stringify([...editedOrgIds].sort()) !==
    JSON.stringify([...(app.organizationIds || [])].sort());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('apps.manageAccess')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {app.name} <span className="text-gray-400">({app.slug})</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add new organization */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('apps.addOrganization')}
            </label>

            {/* Custom Select Dropdown */}
            <div ref={selectRef} className="relative">
              <button
                onClick={() => setIsSelectOpen(!isSelectOpen)}
                disabled={orgsLoading || availableOrgs.length === 0}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-gray-500">
                  {orgsLoading
                    ? t('common.loading')
                    : availableOrgs.length === 0
                      ? t('apps.allOrganizationsAdded')
                      : t('apps.selectOrganization')}
                </span>
                <Plus className="w-5 h-5 text-gray-400" />
              </button>

              {/* Dropdown Menu */}
              {isSelectOpen && availableOrgs.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                  {availableOrgs.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => handleAddOrganization(org.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
                    >
                      {/* Avatar */}
                      {org.imageUrl ? (
                        <img
                          src={org.imageUrl}
                          alt={org.name}
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{org.name}</p>
                        <p className="text-xs text-gray-500 truncate">{org.slug}</p>
                      </div>

                      {/* Members count badge */}
                      <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full flex-shrink-0">
                        <span>{org.membersCount}</span>
                        <span>👥</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-500 mt-2">
              {availableOrgs.length > 0
                ? t('apps.availableOrganizations', { count: availableOrgs.length })
                : t('apps.noNewOrganizations')}
            </p>
          </div>

          {/* Organizations list */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('apps.organizationsWithAccess')} ({editedOrgIds.length})
            </label>

            {editedOrgIds.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500">{t('apps.noOrganizations')}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {t('apps.addOrganizationsDescription')}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {orgsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : (
                  editedOrgIds.map((orgId) => {
                    const org = getOrgById(orgId);
                    return (
                      <div
                        key={orgId}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Avatar */}
                          {org?.imageUrl ? (
                            <img
                              src={org.imageUrl}
                              alt={org.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-white" />
                            </div>
                          )}

                          {/* Infos */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900 truncate">
                                {org?.name || t('apps.unknownOrganization')}
                              </p>
                              {!app.organizationIds.includes(orgId) && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full whitespace-nowrap">
                                  {t('apps.newBadge')}
                                </span>
                              )}
                            </div>
                            <code className="text-xs text-gray-500 truncate block">
                              {org?.slug || orgId}
                            </code>
                          </div>
                        </div>

                        <button
                          onClick={() => handleRemoveOrganization(orgId)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 ml-2"
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Changes indicator */}
          {hasChanges && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <Check className="w-4 h-4" />
                {t('apps.pendingChanges')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {updateMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('apps.saving')}
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {t('common.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
